import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import type { IEntityDataItem } from '~/types/models'
import type { TFieldSetItem, IFieldSetField } from '~/types/fieldSet/models'
import {
  getSelectFromOptionById,
  selectFromOptionIds,
} from '~/services/selectLists/fieldSetFieldSelectFromOptions'
import { getFieldSetFieldShowForRoutesDefault } from '~/services/selectLists/fieldSetFieldShowForRoutes'
import { ensureUniquenessAndPropagateFlagChange } from '~/cynqueModules/ensureUniquenessAndPropagateFlagChange'

const fieldSetFieldListData = undefined as unknown as TFieldSetItem[]

export const getFieldSetFieldListData = getListDataFunctionFactory<
  TFieldSetItem[]
>({
  listData: fieldSetFieldListData,
  storageKey: 'fieldsetfields',
  getStartingListData: getStartingFieldSetFieldListData,
})

export async function getFieldSetFieldListDataForFieldSet(id: string) {
  const listData: TFieldSetItem[] = await getFieldSetFieldListData()
  return listData.filter((fieldSetField) => fieldSetField.fieldSet.id === id)
}

export async function processAfterAddOrUpdateFieldSetFieldListData({
  targetItem: fieldSetField,
  updatedEntityData,
  listData: fieldSetFields,
}: {
  targetItem: IEntityDataItem
  updatedEntityData: Partial<IFieldSetField>
  listData: IFieldSetField[]
}) {
  const fn =
    '[api/msw/api/fieldSetFields/mockData.ts]' +
    ' processAfterAddOrUpdateFieldSetFieldListData |'
  console.debug(`${fn} Post-processing fieldSetFieldListData`)

  const fieldSetId = fieldSetField.fieldSet.id as string // already tested for string
  const fieldSetFieldsOfFieldSet = fieldSetFields.filter(
    (fsf) => fsf.fieldSet.id === fieldSetId
  )

  for (const fieldSetFieldPropertyName of ['isKeyIdentifierField']) {
    if (!updatedEntityData.hasOwnProperty(fieldSetFieldPropertyName)) {
      continue
    }
    await ensureUniquenessAndPropagateFlagChange({
      callingMn: fn,
      sourceEntityId: fieldSetField.id,
      sourceEntityFlagPropertyName: fieldSetFieldPropertyName,
      sourceEntityArray: fieldSetFieldsOfFieldSet,
      sourceEntityData: updatedEntityData,
      targetEntity: undefined,
    })
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'fieldsetfields',
      listData: await getFieldSetFieldListData(),
      callingFn: fn,
    })
  }
}

export async function processAfterDeleteFieldSetFieldListData({
  deletedEntityData,
}: {
  deletedEntityData: IFieldSetField
}) {
  const fn =
    '[api/msw/api/fieldSetFields/mockData.ts]' +
    ' processAfterDeleteFieldSetFieldListData |'
  console.debug(`${fn} Post-processing fieldSetFieldListData`)
  for (const fieldSetFieldPropertyName of ['isKeyIdentifierField']) {
    if (!deletedEntityData[fieldSetFieldPropertyName]) {
      continue
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'fieldsetfields',
      listData: await getFieldSetFieldListData(),
      callingFn: fn,
    })
  }
}

export async function getStartingFieldSetFieldListData(): Promise<
  TFieldSetItem[]
> {
  return [
    {
      id: 'XXFSF0001',
      hrid: 'FSF0001',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 1,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'subtitle1',
      label: 'DIA Info',
      type: 'subtitle',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0002',
      hrid: 'FSF0002',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 2,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'circuitType',
      label: 'Circuit Type',
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString:
        'T1\nFractional T1\nNxT1\nDS3\nFractional DS3\nBurstable DS3\nFastE\nGigE\nOC3\nOC12\nE1\nNxE1\nBurstable E3\nEOC\nEOS\nEOF\nWavelength',
      cols: 6,
      validations: ['required'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0003',
      hrid: 'FSF0003',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 3,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'handoff',
      label: 'Hand-Off',
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString:
        'Electrical - 100Mbps\nElectrical - 1Gbps\nElectrical - 10 Gbps\nSingle Mode - 1 Gbps\nMulti Mode - 1 Gbps\nMulti Mode - 10 Gbps',
      cols: 6,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0004',
      hrid: 'FSF0004',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 4,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'autoNegotiation',
      label: 'Auto Negotiation',
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'On\nOff',
      cols: 6,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0005',
      hrid: 'FSF0005',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 5,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'connector',
      label: 'Connector',
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'RJ45\nOptical',
      cols: 6,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0006',
      hrid: 'FSF0006',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 6,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'vlanSetTo',
      label: 'VLAN Set To',
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Tagged\nUntagged',
      cols: 6,
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0007',
      hrid: 'FSF0007',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 7,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'managed',
      label: 'Managed',
      type: 'checkbox',
      cols: 6,
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0008',
      hrid: 'FSF0008',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 8,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'divider1',
      label: 'Divider 1',
      type: 'divider',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0009',
      hrid: 'FSF0009',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 9,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'circuitId',
      label: 'Circuit ID',
      type: 'textField',
      cols: 6,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: true,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFS00010',
      hrid: 'FSF0010',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 10,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'divider2',
      label: 'Divider 2',
      type: 'divider',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0011',
      hrid: 'FSF0011',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 11,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'gatewayIp',
      label: 'Gateway IP',
      type: 'textField',
      cols: 6,
      validations: ['required', 'ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0012',
      hrid: 'FSF0012',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 12,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'subnetMask',
      label: 'Subnet Mask',
      type: 'textField',
      cols: 6,
      validations: ['subnetMask'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0014',
      hrid: 'FSF0014',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 13,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'startingIp',
      label: 'Starting IP',
      type: 'textField',
      cols: 6,
      validations: ['ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0013',
      hrid: 'FSF0013',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 14,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'broadcastIp',
      label: 'Broadcast IP',
      type: 'textField',
      cols: 6,
      validations: ['ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0015',
      hrid: 'FSF0015',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 15,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'divider3',
      label: 'Divider 3',
      type: 'divider',
      cols: 12,
      showForRoutes: ['Companies', 'Orders'],
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0016',
      hrid: 'FSF0016',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 16,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'subtitle1',
      label: 'Circuit Loop / Termination',
      type: 'subtitle',
      cols: 12,
      showForRoutes: ['Companies', 'Orders'],
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0017',
      hrid: 'FSF0017',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 17,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'lecDemark',
      label: 'LEC Demarc',
      type: 'textField',
      cols: 12,
      showForRoutes: ['Companies', 'Orders'],
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0018',
      hrid: 'FSF0018',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 18,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'carrierEquipment',
      label: 'Carrier Equipment',
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Cisco\nPalo Alto\nCienna\nSwitch\nMVR-OPTI',
      cols: 6,
      showForRoutes: ['Companies', 'Orders'],
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0019',
      hrid: 'FSF0019',
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      seqNum: 19,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'equipmentDetails',
      label: 'Equipment Details',
      type: 'textField',
      cols: 6,
      showForRoutes: ['Companies', 'Orders'],
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0020',
      hrid: 'FSF0020',
      fieldSet: {
        id: 'XXFS0004',
        label: 'IP List',
      },
      seqNum: 1,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'ipAddress',
      label: 'IP Address',
      type: 'textField',
      cols: 6,
      validations: ['ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: true,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0021',
      hrid: 'FSF0021',
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      seqNum: 1,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'gatewayIp',
      label: 'Gateway IP',
      type: 'textField',
      cols: 6,
      validations: ['ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: true,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0022',
      hrid: 'FSF0022',
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      seqNum: 2,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'subnetMask',
      label: 'Subnet Mask',
      type: 'textField',
      cols: 6,
      validations: ['subnetMask'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0023',
      hrid: 'FSF0023',
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      seqNum: 3,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'broadcastIp',
      label: 'Broadcast IP',
      type: 'textField',
      cols: 6,
      validations: ['ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0024',
      hrid: 'FSF0024',
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      seqNum: 4,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'startingIP',
      label: 'Starting IP',
      type: 'textField',
      cols: 6,
      validations: ['ipAddress'],
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },

    {
      id: 'XXFSF0025',
      hrid: 'FSF0025',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'select',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      cols: 6,
      label: 'Integrations',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-01T18:20:00.000Z',
      modifiedAt: '2023-12-01T18:20:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'integrations',
      seqNum: 7,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      isKeyIdentifierField: false,

      itemsString: 'Teams\nSalesforce\nDynamics',
      doAllowMultipleSelections: true,
    },
    {
      id: 'XXFSF0026',
      hrid: 'FSF0026',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Right', width: '1' },
      type: 'select',
      cols: 6,
      label: 'BYOC',
      isKeyIdentifierField: false,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-01T18:22:00.000Z',
      modifiedAt: '2023-12-01T18:22:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'byoc',
      seqNum: 1,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Yes\nNo',
    },
    {
      id: 'XXFSF0027',
      hrid: 'FSF0027',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Provider',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Verizon\nSinch\nIntelePeer',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: {
        id: 'linkRelatedService',
        label: 'Link Related Service',
      },
      actionFieldValue: 'Customer Service',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-01T18:33:00.000Z',
      modifiedAt: '2023-12-01T18:33:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'provider',
      seqNum: 5,
    },
    // {
    //   id: 'XXFSF0028',
    //   hrid: 'FSF0028',
    //   fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
    //   tableHeader: { alignment: 'Center' },
    //   type: 'select',
    //   cols: 6,
    //   label: 'Product',
    //   isKeyIdentifierField: false,
    //   itemsString: 'SIP\nPRI',
    //   action: 'Render if',
    //   actionField: {
    //     id: 'linkRelatedService',
    //     label: 'Link Related Service',
    //   },
    //   actionFieldValue: 'Customer Service',
    //   showForRoutes: getFieldSetFieldShowForRoutesDefault(),
    //   createdAt: '2023-12-01T18:36:00.000Z',
    //   modifiedAt: '2023-12-01T18:36:00.000Z',
    //   canBeDeleted: true,
    //   name: 'product',
    //   seqNum: 6,
    // },
    {
      id: 'XXFSF0029',
      hrid: 'FSF0029',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Right', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Transport Type',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'PTP\nMPLS\nOTT\nSDWAN',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'byoc', label: 'BYOC' },
      actionFieldValue: 'Yes',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-01T18:39:00.000Z',
      modifiedAt: '2023-12-01T18:39:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'transportType',
      seqNum: 5,
    },
    {
      id: 'XXFSF0030',
      hrid: 'FSF0030',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Transport Provider',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString:
        'WXTG Service (Link)\nExisting Customer Service\nCarrier Provided',
      action: 'Render if',
      actionVerb: 'Is not',
      actionField: {
        id: 'linkRelatedService',
        label: 'Link Related Service',
      },
      actionFieldValue: 'Empty',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-01T18:41:00.000Z',
      modifiedAt: '2023-12-01T18:41:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'transportProvider',
      seqNum: 3,
    },
    {
      id: 'XXFSF0031',
      hrid: 'FSF0031',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Center', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Transport Carrier',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Crown Castle\nCoeo\nComcast',
      action: 'Render if',
      actionVerb: 'Is',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-01T18:45:00.000Z',
      modifiedAt: '2023-12-01T18:45:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'transportCarrier',
      seqNum: 6,
      actionField: {
        id: 'linkRelatedService',
        label: 'Link Related Service',
      },
      actionFieldValue: 'Customer Service',
    },
    {
      id: 'XXFSF0032',
      hrid: 'FSF0032',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Site Type',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-02T16:23:00.000Z',
      modifiedAt: '2023-12-02T16:23:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'siteType',
      seqNum: 1,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Hub\nSpoke',
    },
    {
      id: 'XXFSF0033',
      hrid: 'FSF0033',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Local Breakout',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Yes\nNo',
      showForRoutes: ['Companies', 'Orders'],
      createdAt: '2023-12-02T16:37:00.000Z',
      modifiedAt: '2023-12-02T16:37:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'localBreakout',
      seqNum: 2,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0035',
      hrid: 'FSF0035',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Right', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Firewall',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Stateful Firewall\nNo\nNextGen Firewall (NGF)',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-02T16:44:00.000Z',
      modifiedAt: '2023-12-02T16:44:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'firewall',
      seqNum: 4,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0036',
      hrid: 'FSF0036',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Right', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Hardware Deployment',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Virtual Device\nCloud Device\nPhysical Device',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-02T16:47:00.000Z',
      modifiedAt: '2023-12-02T16:47:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'hardwareDeployment',
      seqNum: 7,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0037',
      hrid: 'FSF0037',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'subtitle',
      cols: 12,
      label: 'Security Configuration',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'firewall', label: 'Firewall' },
      actionFieldValue: 'NextGen Firewall (NGF)',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-02T16:50:00.000Z',
      modifiedAt: '2023-12-02T16:50:00.000Z',
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: true,
      name: 'securityConfiguration',
      seqNum: 9,
    },
    {
      id: 'XXFSF0038',
      hrid: 'FSF0038',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Center', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Firewall Vendor',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Native\nPalo Alto\nZscaler',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'firewall', label: 'Firewall' },
      actionFieldValue: 'NextGen Firewall (NGF)',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-02T16:53:00.000Z',
      modifiedAt: '2023-12-02T16:53:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'firewallVendor',
      seqNum: 10,
    },
    {
      id: 'XXFSF0040',
      hrid: 'FSF0040',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'divider',
      cols: 12,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'firewall', label: 'Firewall' },
      actionFieldValue: 'NextGen Firewall (NGF)',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T21:49:00.000Z',
      modifiedAt: '2023-12-03T21:49:00.000Z',
      version: '1',
      canBeDeleted: true,
      label: 'Divider',
      name: 'divider2',
      seqNum: 8,
    },
    {
      id: 'XXFSF0041',
      hrid: 'FSF0041',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Center', width: '1' },
      type: 'select',
      cols: 6,
      label: 'WAN IP Addresses',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Select Circuits\nAdd New',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T21:59:00.000Z',
      modifiedAt: '2023-12-03T21:59:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'wanIpAddresses',
      seqNum: 16,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0042',
      hrid: 'FSF0042',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'select',
      cols: 6,
      label: 'LAN IP Addresses',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'N/A\nAdd New',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:02:00.000Z',
      modifiedAt: '2023-12-03T22:02:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'lanIpAddresses',
      seqNum: 17,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0043',
      hrid: 'FSF0043',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'textField',
      cols: 6,
      label: 'Ports_Table',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:04:00.000Z',
      modifiedAt: '2023-12-03T22:04:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'portsTable',
      seqNum: 18,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0044',
      hrid: 'FSF0044',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Center', width: '1' },
      type: 'select',
      cols: 6,
      label: 'UTM',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Yes\nNo',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:05:00.000Z',
      modifiedAt: '2023-12-03T22:05:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'utm',
      seqNum: 11,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'firewall', label: 'Firewall' },
      actionFieldValue: 'NextGen Firewall (NGF)',
    },
    {
      id: 'XXFSF0045',
      hrid: 'FSF0045',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'select',
      cols: 6,
      label: 'IPS_IDS',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:05:00.000Z',
      modifiedAt: '2023-12-03T22:05:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'ipsIds',
      seqNum: 12,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Yes\nNo',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'firewall', label: 'Firewall' },
      actionFieldValue: 'NextGen Firewall (NGF)',
    },
    {
      id: 'XXFSF0046',
      hrid: 'FSF0046',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Web Filtering',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:06:00.000Z',
      modifiedAt: '2023-12-03T22:06:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'webFiltering',
      seqNum: 13,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Yes\nNo',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'firewall', label: 'Firewall' },
      actionFieldValue: 'NextGen Firewall (NGF)',
    },
    {
      id: 'XXFSF0047',
      hrid: 'FSF0047',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'subtitle',
      cols: 12,
      label: 'Configuration Details',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:06:00.000Z',
      modifiedAt: '2023-12-03T22:06:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'configurationDetails',
      seqNum: 15,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0048',
      hrid: 'FSF0048',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'select',
      cols: 6,
      label: 'Purchase Model',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Rental\nPurchase\nLease',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T22:08:00.000Z',
      modifiedAt: '2023-12-03T22:08:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'purchaseModel',
      seqNum: 5,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0049',
      hrid: 'FSF0049',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'select',
      cols: 6,
      label: 'BYOC Service',
      isKeyIdentifierField: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'byoc', label: 'BYOC' },
      actionFieldValue: 'Yes',
      showForRoutes: ['Companies', 'Opportunities', 'Orders'],
      createdAt: '2024-03-03T00:04:00.000Z',
      modifiedAt: '2024-03-03T00:04:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'byocService',
      seqNum: 2,
      showForRoutesDescr: 'COO',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'WXTG Service\nExisting Customer Service',
      doAllowMultipleSelections: false,
    },
    {
      id: 'XXFSF0050',
      hrid: 'FSF0050',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Auto Attendant',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:26:00.000Z',
      modifiedAt: '2023-12-03T23:26:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'autoAttendant',
      seqNum: 12,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Yes\nNo',
      doAllowMultipleSelections: false,
    },
    {
      id: 'XXFSF0052',
      hrid: 'FSF0052',
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      type: 'select',
      cols: 6,
      label: 'Deployment Method',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Virtual\nPhysical\nCloud',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:36:00.000Z',
      modifiedAt: '2023-12-03T23:36:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'deploymentMethod',
      seqNum: 3,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0053',
      hrid: 'FSF0053',
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      type: 'textField',
      cols: 6,
      label: 'Serial Number',
      isKeyIdentifierField: true,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:37:00.000Z',
      modifiedAt: '2023-12-03T23:37:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'serialNumber',
      seqNum: 2,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0054',
      hrid: 'FSF0054',
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      type: 'textField',
      cols: 6,
      label: 'Device Hardware',
      isKeyIdentifierField: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'deploymentMethod', label: 'Deployment Method' },
      actionFieldValue: 'Physical',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:37:00.000Z',
      modifiedAt: '2023-12-03T23:37:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'deviceHardware',
      seqNum: 4,
    },
    {
      id: 'XXFSF00555',
      hrid: 'FSF0055',
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      type: 'select',
      cols: 6,
      label: 'Purchase Model',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Rental\nPurchase\nLease',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'deploymentMethod', label: 'Deployment Method' },
      actionFieldValue: 'Physical',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:38:00.000Z',
      modifiedAt: '2023-12-03T23:38:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'purchaseModel',
      seqNum: 5,
    },
    {
      id: 'XXFSF0056',
      hrid: 'FSF0056',
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      type: 'select',
      cols: 6,
      label: 'SDWAN Max Throughput',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: '25Mb\n100Mb\n1Gb\n10Gb',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:40:00.000Z',
      modifiedAt: '2023-12-03T23:40:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'sdwanMaxThroughput',
      seqNum: 6,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0057',
      hrid: 'FSF0057',
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      type: 'select',
      cols: 6,
      label: 'Max Throughput with Security',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: '25Mb\n50Mb\n100Mb\n250Mb\n1Gb',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-03T23:41:00.000Z',
      modifiedAt: '2023-12-03T23:41:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'maxThroughputWithSecurity',
      seqNum: 7,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0058',
      hrid: 'FSF0058',
      fieldSet: { id: 'XXFS0003', label: 'SDWAN' },
      type: 'divider',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2023-12-05T20:52:00.000Z',
      modifiedAt: '2023-12-05T20:52:00.000Z',
      version: '1',
      canBeDeleted: true,
      label: 'Divider',
      name: 'divider',
      seqNum: 14,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    // {
    //   id: 'XXFSF0059',
    //   hrid: 'FSF0059',
    //   fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
    //   tableHeader: { alignment: 'Left', width: '1' },
    //   type: 'select',
    //   cols: 6,
    //   label: 'Link Related Service',
    //   isKeyIdentifierField: false,
    //   action: 'Enable if',
    //   actionField: { id: 'byoc', label: 'BYOC' },
    //   actionFieldValue: 'Yes',
    //   showForRoutes: getFieldSetFieldShowForRoutesDefault(),
    //   createdAt: '2024-03-03T00:04:00.000Z',
    //   modifiedAt: '2024-03-03T00:04:00.000Z',
    //   canBeDeleted: true,
    //   name: 'linkRelatedService',
    //   seqNum: 4,
    //   itemsString: 'WXTG Service\nCustomer Service',
    //   doAllowMultipleSelections: false,
    // },
    {
      id: 'XXFSF0060',
      hrid: 'FSF0060',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'numberTextField',
      cols: 6,
      label: 'Phone Number',
      showForRoutes: ['Companies', 'Orders'],
      createdAt: '2024-03-03T00:52:00.000Z',
      modifiedAt: '2024-03-03T00:52:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'phoneNumber',
      seqNum: 1,
      isKeyIdentifierField: false,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0061',
      hrid: 'FSF0061',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'textField',
      cols: 6,
      label: 'Location',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2024-03-03T00:53:00.000Z',
      modifiedAt: '2024-03-03T00:53:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'location',
      seqNum: 2,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0062',
      hrid: 'FSF0062',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'select',
      cols: 6,
      label: 'Country',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'US\nCanada',
      doAllowMultipleSelections: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2024-03-03T00:53:00.000Z',
      modifiedAt: '2024-03-03T00:53:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'country',
      seqNum: 3,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0063',
      hrid: 'FSF0063',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'textField',
      cols: 6,
      label: 'Purpose',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2024-03-03T00:54:00.000Z',
      modifiedAt: '2024-03-03T00:54:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'purpose',
      seqNum: 4,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0064',
      hrid: 'FSF0064',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      tableHeader: { alignment: 'Left', width: '1' },
      type: 'textField',
      cols: 6,
      label: 'Call Diagram',
      isKeyIdentifierField: false,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2024-03-03T00:54:00.000Z',
      modifiedAt: '2024-03-03T00:54:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'callDiagram',
      seqNum: 5,
    },
    {
      id: 'XXFSF0065',
      hrid: 'FSF0065',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'textField',
      cols: 6,
      label: 'Carrier',
      isKeyIdentifierField: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2024-03-03T00:54:00.000Z',
      modifiedAt: '2024-03-03T00:54:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'carrier',
      seqNum: 6,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0066',
      hrid: 'FSF0066',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'select',
      cols: 6,
      label: 'Ring To',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'DNIS\nPhone Number',
      doAllowMultipleSelections: false,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2024-03-03T00:55:00.000Z',
      modifiedAt: '2024-03-03T00:55:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'ringTo',
      seqNum: 7,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
    },
    {
      id: 'XXFSF0067',
      hrid: 'FSF0067',
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      type: 'select',
      cols: 6,
      label: 'Numbers',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: '1\n2\n3',
      doAllowMultipleSelections: true,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      showForRoutes: ['Companies', 'Opportunities'],
      createdAt: '2024-03-05T19:40:00.000Z',
      modifiedAt: '2024-03-05T19:40:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'numbers',
      seqNum: 8,
    },

    {
      id: 'XXFSF0068',
      hrid: 'FSF0068',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Directory',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'DTMF\nSpeech Reg\nNone',
      doAllowMultipleSelections: false,
      showForRoutes: ['Companies', 'Orders'],
      createdAt: '2024-03-09T23:21:00.000Z',
      modifiedAt: '2024-03-09T23:21:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'directory',
      seqNum: 15,
      showForRoutesDescr: 'C-O',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'autoAttendant', label: 'Auto Attendant' },
      actionFieldValue: 'Yes',
    },
    {
      id: 'XXFSF0069',
      hrid: 'FSF0069',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'divider',
      cols: 12,
      label: 'Divider',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'autoAttendant', label: 'Auto Attendant' },
      actionFieldValue: 'Yes',
      showForRoutes: ['Companies', 'Opportunities', 'Orders'],
      createdAt: '2024-03-09T23:23:00.000Z',
      modifiedAt: '2024-03-09T23:23:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: '',
      seqNum: 10,
      showForRoutesDescr: 'COO',
    },
    {
      id: 'XXFSF0070',
      hrid: 'FSF0070',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      tableHeader: { alignment: 'Center', width: '1' },
      type: 'select',
      cols: 6,
      label: 'Link Related Service',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Linked Voice Service',
      doAllowMultipleSelections: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'byocService', label: 'BYOC Service' },
      actionFieldValue: 'WXTG Service',
      showForRoutes: ['Companies', 'Opportunities', 'Orders'],
      createdAt: '2024-03-09T23:27:00.000Z',
      modifiedAt: '2024-03-09T23:27:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'linkRelatedService',
      seqNum: 4,
      showForRoutesDescr: 'COO',
    },
    {
      id: 'XXFSF0071',
      hrid: 'FSF0071',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'subtitle',
      cols: 12,
      label: 'Auto Attendants',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'autoAttendant', label: 'Auto Attendant' },
      actionFieldValue: 'Yes',
      showForRoutes: ['Companies', 'Opportunities', 'Orders'],
      createdAt: '2024-03-09T23:28:00.000Z',
      modifiedAt: '2024-03-09T23:28:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'autoAttendants',
      seqNum: 11,
      showForRoutesDescr: 'COO',
    },
    {
      id: 'XXFSF0072',
      hrid: 'FSF0072',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'textField',
      cols: 6,
      label: 'Purpose',
      isKeyIdentifierField: false,
      showForRoutes: ['Companies', 'Orders'],
      createdAt: '2024-03-09T23:33:00.000Z',
      modifiedAt: '2024-03-09T23:33:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'purpose',
      seqNum: 13,
      showForRoutesDescr: 'C-O',
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'autoAttendant', label: 'Auto Attendant' },
      actionFieldValue: 'Yes',
    },
    {
      id: 'XXFSF0073',
      hrid: 'FSF0073',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Phone Number',
      isKeyIdentifierField: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'autoAttendant', label: 'Auto Attendant' },
      actionFieldValue: 'Yes',
      showForRoutes: ['Companies', 'Orders'],
      createdAt: '2024-03-09T23:34:00.000Z',
      modifiedAt: '2024-03-09T23:34:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'phoneNumber',
      seqNum: 14,
      showForRoutesDescr: 'C-O',
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Validate Phone Number',
      doAllowMultipleSelections: false,
    },
    {
      id: 'XXFSF0074',
      hrid: 'FSF0074',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Call Flow Diagram',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Document Link',
      doAllowMultipleSelections: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'autoAttendant', label: 'Auto Attendant' },
      actionFieldValue: 'Yes',
      showForRoutes: ['Companies', 'Orders'],
      createdAt: '2024-03-09T23:36:00.000Z',
      modifiedAt: '2024-03-09T23:36:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'callFlowDiagram',
      seqNum: 16,
      showForRoutesDescr: 'C-O',
    },
    {
      id: 'XXFSF0075',
      hrid: 'FSF0075',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Teams Integration',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Direct Routing\nPlug-in\nOther',
      doAllowMultipleSelections: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'integrations', label: 'Integrations' },
      actionFieldValue: 'Teams',
      showForRoutes: ['Companies', 'Opportunities', 'Orders'],
      createdAt: '2024-03-09T23:42:00.000Z',
      modifiedAt: '2024-03-09T23:42:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'teamsIntegration',
      seqNum: 8,
      showForRoutesDescr: 'COO',
    },
    {
      id: 'XXFSF0076',
      hrid: 'FSF0076',
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      type: 'select',
      cols: 6,
      label: 'Salesforce Integration',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Service Cloud Voice\nOut-of-the-box\nCustom',
      doAllowMultipleSelections: false,
      action: 'Render if',
      actionVerb: 'Is',
      actionField: { id: 'integrations', label: 'Integrations' },
      actionFieldValue: 'Salesforce',
      showForRoutes: ['Companies', 'Opportunities', 'Orders'],
      createdAt: '2024-03-09T23:43:00.000Z',
      modifiedAt: '2024-03-09T23:43:00.000Z',
      version: '1',
      canBeDeleted: true,
      name: 'salesforceIntegration',
      seqNum: 9,
      showForRoutesDescr: 'COO',
    },
    {
      id: 'XXFSF0077',
      hrid: 'FSF0077',
      fieldSet: {
        id: 'XXFS0009',
        label: 'Circuit',
      },
      seqNum: 1,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'provisioningAndImplementation',
      label: 'Provisioning and Implementation',
      type: 'subtitle',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:32:35.982Z',
      modifiedAt: '2025-03-23T22:32:35.982Z',
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0078',
      hrid: 'FSF0078',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'select',
      doAllowMultipleSelections: false,
      cols: 6,
      label: 'Status',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Not Started\nIn Progress\nDone',
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:36:09.859Z',
      modifiedAt: '2025-03-23T22:36:09.859Z',
      version: '1',
      canBeDeleted: true,
      name: 'status',
      seqNum: 2,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0079',
      hrid: 'FSF0079',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'dateField',
      cols: 6,
      label: 'Completed On',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      action: 'Enable if',
      actionVerb: 'Is',
      actionField: { id: 'status', label: 'Status' },
      actionFieldValue: 'Done',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:36:09.859Z',
      modifiedAt: '2025-03-23T22:36:09.859Z',
      version: '1',
      canBeDeleted: true,
      name: 'completedOn',
      seqNum: 3,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0080',
      hrid: 'FSF0080',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'textField',
      cols: 12,
      label: 'Notes',
      isKeyIdentifierField: false,
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:36:52.270Z',
      modifiedAt: '2025-03-23T22:36:52.270Z',
      version: '1',
      canBeDeleted: true,
      name: 'notes',
      seqNum: 4,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0081',
      hrid: 'FSF0081',
      fieldSet: {
        id: 'XXFS0009',
        label: 'Circuit',
      },
      seqNum: 5,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'divider1',
      label: 'Divider 1',
      type: 'divider',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-24T00:39:58.207Z',
      modifiedAt: '2025-03-24T00:39:58.207Z',
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    //
    //
    //
    {
      id: 'XXFSF0082',
      hrid: 'FSF0082',
      fieldSet: {
        id: 'XXFS0009',
        label: 'Circuit',
      },
      seqNum: 6,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'serviceTurnUpAndCompletion',
      label: 'Service Turn_Up and Completion',
      type: 'subtitle',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:40:35.982Z',
      modifiedAt: '2025-03-23T22:40:35.982Z',
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0083',
      hrid: 'FSF0083',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'select',
      doAllowMultipleSelections: false,
      cols: 6,
      label: 'Status',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Not Started\nIn Progress\nDone',
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:41:09.859Z',
      modifiedAt: '2025-03-23T22:41:09.859Z',
      version: '1',
      canBeDeleted: true,
      name: 'status1',
      seqNum: 7,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0084',
      hrid: 'FSF0084',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'dateField',
      cols: 6,
      label: 'Completed On',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      action: 'Enable if',
      actionVerb: 'Is',
      actionField: { id: 'status1', label: 'Status' },
      actionFieldValue: 'Done',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:42:09.859Z',
      modifiedAt: '2025-03-23T22:42:09.859Z',
      version: '1',
      canBeDeleted: true,
      name: 'completedOn1',
      seqNum: 8,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0085',
      hrid: 'FSF0085',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'textField',
      cols: 12,
      label: 'Notes',
      isKeyIdentifierField: false,
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:43:52.270Z',
      modifiedAt: '2025-03-23T22:43:52.270Z',
      version: '1',
      canBeDeleted: true,
      name: 'notes1',
      seqNum: 9,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0081',
      hrid: 'FSF0081',
      fieldSet: {
        id: 'XXFS0009',
        label: 'Circuit',
      },
      seqNum: 10,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'divider2',
      label: 'Divider 2',
      type: 'divider',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-24T00:44:58.207Z',
      modifiedAt: '2025-03-24T00:44:58.207Z',
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    //
    //
    //
    {
      id: 'XXFSF0087',
      hrid: 'FSF0087',
      fieldSet: {
        id: 'XXFS0009',
        label: 'Circuit',
      },
      seqNum: 11,
      action: null,
      actionField: null,
      actionVerb: null,
      actionFieldValue: null,
      name: 'retiringOfLegacyServices',
      label: 'Retiring of Legacy Services',
      type: 'subtitle',
      cols: 12,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:45:35.982Z',
      modifiedAt: '2025-03-23T22:45:35.982Z',
      isKeyIdentifierField: false,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXFSF0088',
      hrid: 'FSF0088',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'select',
      doAllowMultipleSelections: false,
      cols: 6,
      label: 'Status',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      itemsString: 'Not Started\nIn Progress\nDone',
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:46:09.859Z',
      modifiedAt: '2025-03-23T22:46:09.859Z',
      version: '1',
      canBeDeleted: true,
      name: 'status2',
      seqNum: 12,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0089',
      hrid: 'FSF0089',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'dateField',
      cols: 6,
      label: 'Completed On',
      isKeyIdentifierField: false,
      selectFrom: getSelectFromOptionById(selectFromOptionIds.CUSTOM),
      action: 'Enable if',
      actionVerb: 'Is',
      actionField: { id: 'status1', label: 'Status' },
      actionFieldValue: 'Done',
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:47:09.859Z',
      modifiedAt: '2025-03-23T22:47:09.859Z',
      version: '1',
      canBeDeleted: true,
      name: 'completedOn2',
      seqNum: 13,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0090',
      hrid: 'FSF0090',
      fieldSet: { id: 'XXFS0009', label: 'Circuit' },
      type: 'textField',
      cols: 12,
      label: 'Notes',
      isKeyIdentifierField: false,
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-03-23T22:48:52.270Z',
      modifiedAt: '2025-03-23T22:48:52.270Z',
      version: '1',
      canBeDeleted: true,
      name: 'notes2',
      seqNum: 14,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0091',
      hrid: 'FSF0091',
      fieldSet: { id: 'XXFS0010', label: 'POTS Line' },
      type: 'textField',
      cols: 6,
      label: 'Phone Number',
      isKeyIdentifierField: true,
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-08-28T01:59:49.197Z',
      modifiedAt: '2025-08-28T01:59:49.197Z',
      version: '1',
      canBeDeleted: true,
      name: 'phoneNumber',
      seqNum: 1,
      tableHeader: { alignment: 'Left', width: '1' },
    },
    {
      id: 'XXFSF0092',
      hrid: 'FSF0092',
      fieldSet: { id: 'XXFS0010', label: 'POTS Line' },
      type: 'textField',
      cols: 6,
      label: 'Circuit ID',
      isKeyIdentifierField: false,
      action: null,
      actionVerb: null,
      actionField: null,
      actionFieldValue: null,
      showForRoutes: getFieldSetFieldShowForRoutesDefault(),
      createdAt: '2025-08-28T01:59:49.197Z',
      modifiedAt: '2025-08-28T01:59:49.197Z',
      version: '1',
      canBeDeleted: true,
      name: 'circuitId',
      seqNum: 2,
      tableHeader: { alignment: 'Left', width: '1' },
    },
  ]
}
